import { SupportFacets } from './support-area.util';

/**
 * Facets meeting at a sharper angle than this [°] keep their own normals where
 * they meet, so a sharp edge keeps a sharp support boundary.
 */
export const SUPPORT_CREASE_ANGLE_DEG = 40;

/**
 * Normals per vertex for colouring the support region smoothly: the
 * area-weighted mean of the facet normals around the vertex, so the overhang
 * test can vary across a triangle instead of flipping from one triangle to
 * the next. Returns x, y, z, w per vertex, in the frame `facets` were
 * measured in.
 *
 * Vertices at the same position count as one, so a mesh that stores its
 * vertices per triangle or per face is smoothed across those seams too.
 * Facets turned more than `creaseAngleDeg` from the vertex's own facets are
 * left out. Where the vertex's own facets already meet at a crease, one normal
 * cannot serve them all: w is 0 and xyz zero, meaning "use each facet's own
 * normal here". Otherwise w is 1 and xyz is a unit normal. Vertices of
 * degenerate triangles only are all zero.
 */
export function supportVertexNormals(
  positions: ArrayLike<number>,
  index: ArrayLike<number> | null,
  facets: SupportFacets,
  creaseAngleDeg = SUPPORT_CREASE_ANGLE_DEG,
): Float32Array {
  const vertexCount = Math.floor(positions.length / 3);
  const { normals, areas } = facets;
  const triangles = areas.length;
  const corner = index ? (i: number): number => index[i] : (i: number): number => i;
  const weld = weldByPosition(positions, vertexCount);

  // Each vertex's own facets (sum of area × normal, and of area), and the
  // facets around each welded position as a list: ring[ringStart[g]…ringStart[g + 1]).
  const own = new Float64Array(vertexCount * 4);
  const ringStart = new Uint32Array(weld.count + 1);
  for (let t = 0; t < triangles; t++) {
    const area = areas[t];
    if (!(area > 0)) continue;
    for (let c = 0; c < 3; c++) {
      const v = corner(t * 3 + c);
      own[v * 4] += normals[t * 3] * area;
      own[v * 4 + 1] += normals[t * 3 + 1] * area;
      own[v * 4 + 2] += normals[t * 3 + 2] * area;
      own[v * 4 + 3] += area;
      ringStart[weld.ids[v] + 1]++;
    }
  }
  for (let g = 0; g < weld.count; g++) ringStart[g + 1] += ringStart[g];
  const ring = new Uint32Array(ringStart[weld.count]);
  const next = ringStart.slice(0, weld.count);
  for (let t = 0; t < triangles; t++) {
    if (!(areas[t] > 0)) continue;
    for (let c = 0; c < 3; c++) ring[next[weld.ids[corner(t * 3 + c)]]++] = t;
  }

  const crease = (creaseAngleDeg * Math.PI) / 180;
  const cosCrease = Math.cos(crease);
  // Facets split evenly across a crease of creaseAngleDeg average to this share of their area.
  const minCoherence = Math.cos(crease / 2);
  const out = new Float32Array(vertexCount * 4);
  for (let v = 0; v < vertexCount; v++) {
    const rx = own[v * 4], ry = own[v * 4 + 1], rz = own[v * 4 + 2];
    const refLength = Math.sqrt(rx * rx + ry * ry + rz * rz);
    if (!(refLength > 0) || refLength < minCoherence * own[v * 4 + 3]) continue;
    let x = 0, y = 0, z = 0;
    const g = weld.ids[v];
    for (let k = ringStart[g]; k < ringStart[g + 1]; k++) {
      const t = ring[k];
      const nx = normals[t * 3], ny = normals[t * 3 + 1], nz = normals[t * 3 + 2];
      if (nx * rx + ny * ry + nz * rz < cosCrease * refLength) continue;
      x += nx * areas[t];
      y += ny * areas[t];
      z += nz * areas[t];
    }
    const length = Math.sqrt(x * x + y * y + z * z);
    if (!(length > 0)) continue;
    out[v * 4] = x / length;
    out[v * 4 + 1] = y / length;
    out[v * 4 + 2] = z / length;
    out[v * 4 + 3] = 1;
  }
  return out;
}

/** Numbers each distinct position (compared as float32, −0 as 0) and gives every vertex its number. */
function weldByPosition(positions: ArrayLike<number>, vertexCount: number): { ids: Uint32Array; count: number } {
  const floats = positions instanceof Float32Array ? positions : Float32Array.from(positions);
  const bits = new Uint32Array(floats.buffer, floats.byteOffset, vertexCount * 3);
  const key = (i: number): number => (bits[i] === 0x80000000 ? 0 : bits[i]);
  let size = 1;
  while (size < vertexCount * 2) size *= 2;
  const mask = size - 1;
  // Open addressing: slot → first vertex seen at that position, plus one; 0 is empty.
  const slots = new Uint32Array(size);
  const ids = new Uint32Array(vertexCount);
  let count = 0;
  for (let v = 0; v < vertexCount; v++) {
    const x = key(v * 3), y = key(v * 3 + 1), z = key(v * 3 + 2);
    let h = Math.imul(x, 0x9e3779b1) ^ Math.imul(y, 0x85ebca77) ^ Math.imul(z, 0xc2b2ae3d);
    h = (h ^ (h >>> 15)) & mask;
    for (;;) {
      const first = slots[h];
      if (first === 0) {
        slots[h] = v + 1;
        ids[v] = count++;
        break;
      }
      const u = first - 1;
      if (key(u * 3) === x && key(u * 3 + 1) === y && key(u * 3 + 2) === z) {
        ids[v] = ids[u];
        break;
      }
      h = (h + 1) & mask;
    }
  }
  return { ids, count };
}
